import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { Navigate, NavLink } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardContent,
  Chip as MuiChip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider as MuiDivider,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import agent from "../../utils/axios";
import { useMaintenanceAccess } from "../../hooks/useMaintenanceAccess";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const Chip = styled(MuiChip)(spacing);

/** Mirrors Core.Entities.MaintenanceJobRun (camelCase over the wire). */
interface JobRun {
  id: string;
  jobName: string;
  state: "Running" | "Completed" | "Cancelled" | "Failed" | "Interrupted";
  dryRun: boolean;
  startedBy: string | null;
  startedAt: string;
  finishedAt: string | null;
  heartbeatAt: string;
  candidatesAtStart: number;
  payeesProcessed: number;
  payeesSkipped: number;
  payeesUpdated: number;
  payeesFailed: number;
  billsAssigned: number;
  remaining: number;
  message: string | null;
  failures: {
    payeeId: string;
    billRefCode: string | null;
    reason: string;
    at: string;
  }[];
}

interface JobStatus {
  jobName: string;
  isRunning: boolean;
  current: JobRun | null;
  history: JobRun[];
}

const POLL_MS = 3000;

const stateColor = (
  state: JobRun["state"]
): "default" | "primary" | "success" | "warning" | "error" => {
  switch (state) {
    case "Running":
      return "primary";
    case "Completed":
      return "success";
    case "Cancelled":
    case "Interrupted":
      return "warning";
    case "Failed":
      return "error";
    default:
      return "default";
  }
};

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString("en-US");
const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString() : "—";
const elapsed = (run: JobRun) => {
  const start = new Date(run.startedAt).getTime();
  const end = run.finishedAt
    ? new Date(run.finishedAt).getTime()
    : new Date(run.heartbeatAt).getTime();
  const s = Math.max(0, Math.round((end - start) / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
      <Typography variant="h4">{value}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  </Grid>
);

function JobCard({ name, title }: { name: string; title: string }) {
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s: JobStatus = await agent.Maintenance.status(name);
      setStatus(s);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? "Could not load job status.");
    }
  }, [name]);

  // Poll while a run is active; otherwise just load once.
  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (status?.isRunning) {
      if (!timer.current) {
        timer.current = setInterval(refresh, POLL_MS);
      }
    } else if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [status?.isRunning, refresh]);

  const start = async (dryRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await agent.Maintenance.start(name, dryRun);
      await refresh();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ?? e?.message ?? "Could not start the job."
      );
    } finally {
      setBusy(false);
      setConfirmApply(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await agent.Maintenance.cancel(name);
      await refresh();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ?? e?.message ?? "Could not cancel the job."
      );
    } finally {
      setBusy(false);
    }
  };

  const run = status?.current ?? null;
  const running = status?.isRunning ?? false;
  const progress =
    run && run.candidatesAtStart > 0
      ? Math.min(100, (run.payeesProcessed / run.candidatesAtStart) * 100)
      : 0;

  return (
    <Card mb={6}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            {run && (
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  size="small"
                  label={run.state}
                  color={stateColor(run.state)}
                />
                {run.dryRun && (
                  <Chip size="small" variant="outlined" label="dry run" />
                )}
                <Typography variant="body2" color="text.secondary">
                  started {when(run.startedAt)} by {run.startedBy ?? "—"} ·{" "}
                  {elapsed(run)}
                </Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              disabled={busy || running}
              onClick={() => start(true)}
            >
              Dry run
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={busy || running}
              onClick={() => setConfirmApply(true)}
            >
              Run and apply
            </Button>
            <Button
              variant="outlined"
              color="warning"
              disabled={busy || !running}
              onClick={cancel}
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {run?.state === "Interrupted" && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {run.message}
          </Alert>
        )}

        {running && (
          <Box mt={4}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              {fmt(run?.payeesProcessed)} of {fmt(run?.candidatesAtStart)}{" "}
              payees processed ({Math.round(progress)}%) · refreshes every 3s
            </Typography>
          </Box>
        )}

        {run && (
          <Grid container spacing={3} mt={2}>
            <Stat label="Candidates" value={fmt(run.candidatesAtStart)} />
            <Stat label="Processed" value={fmt(run.payeesProcessed)} />
            <Stat
              label={run.dryRun ? "Codes needed" : "Codes assigned"}
              value={fmt(run.billsAssigned)}
            />
            <Stat label="Payees updated" value={fmt(run.payeesUpdated)} />
            <Stat label="Remaining" value={fmt(run.remaining)} />
            <Stat
              label={`Failed${
                run.payeesSkipped ? ` / skipped ${fmt(run.payeesSkipped)}` : ""
              }`}
              value={fmt(run.payeesFailed)}
            />
          </Grid>
        )}

        {run?.message && run.state !== "Interrupted" && (
          <Typography variant="body2" mt={3}>
            {run.message}
          </Typography>
        )}

        {run && run.failures?.length > 0 && (
          <Box mt={4}>
            <Typography variant="subtitle2" gutterBottom>
              Errored bills ({fmt(run.failures.length)}
              {run.failures.length >= 500 ? ", first 500 shown" : ""})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Payee</TableCell>
                  <TableCell>Bill Ref Code</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {run.failures.map((f, i) => (
                  <TableRow key={`${f.payeeId}-${f.billRefCode}-${i}`}>
                    <TableCell>
                      <Link
                        component={NavLink}
                        to={`/payees/detail/${f.payeeId}`}
                      >
                        {f.payeeId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {f.billRefCode ? (
                        <Link
                          component={NavLink}
                          to={`/bills/detail/${f.billRefCode}`}
                        >
                          {f.billRefCode}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{f.reason}</TableCell>
                    <TableCell>{when(f.at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {status && status.history.length > 0 && (
          <Box mt={4}>
            <Typography variant="subtitle2" gutterBottom>
              Previous runs
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>State</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Finished</TableCell>
                  <TableCell align="right">Assigned</TableCell>
                  <TableCell align="right">Updated</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status.history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={h.state}
                        color={stateColor(h.state)}
                      />
                    </TableCell>
                    <TableCell>{h.dryRun ? "dry run" : "apply"}</TableCell>
                    <TableCell>{when(h.startedAt)}</TableCell>
                    <TableCell>{when(h.finishedAt)}</TableCell>
                    <TableCell align="right">{fmt(h.billsAssigned)}</TableCell>
                    <TableCell align="right">{fmt(h.payeesUpdated)}</TableCell>
                    <TableCell align="right">{fmt(h.payeesFailed)}</TableCell>
                    <TableCell>{h.startedBy ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {!run && status && (
          <Typography variant="body2" color="text.secondary" mt={3}>
            This job has not been run in this environment yet.
          </Typography>
        )}
      </CardContent>

      <Dialog open={confirmApply} onClose={() => setConfirmApply(false)}>
        <DialogTitle>Run and apply?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This writes to the database in this environment: every ACH bill that
            is Paid and has no Payment Ref Code will receive one. It runs in the
            background and can be cancelled at a page boundary; it is safe to
            run again. Run a dry run first if you have not.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApply(false)}>Back</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={() => start(false)}
          >
            Yes, apply
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function Maintenance() {
  const { loading, canOperate, jobs, user } = useMaintenanceAccess();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canOperate) {
    return <Navigate to="/" replace />;
  }

  return (
    <React.Fragment>
      <Helmet title="Maintenance" />
      <Typography variant="h3" gutterBottom display="inline">
        Maintenance
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Dashboard
        </Link>
        <Typography>Maintenance</Typography>
      </Breadcrumbs>
      <Typography variant="body2" color="text.secondary" mt={2}>
        One-off data jobs that run inside the API. Signed in as {user}. Progress
        is saved after every page, so a job interrupted by a restart is resumed
        by starting it again.
      </Typography>
      <Divider my={6} />
      {jobs.map((j) => (
        <JobCard key={j.name} name={j.name} title={j.title} />
      ))}
    </React.Fragment>
  );
}

export default Maintenance;
